/**
 * The viewer's read state, and advancing it.
 *
 * `GET /:cid/unread` recomputes the count from the messages that exist right now
 * instead of reading a maintained counter, so it cannot drift.
 *
 * `POST /:cid/read` never moves the cursor backwards, so two tabs racing cannot
 * re-light a badge, and a repeated call is a no-op. That is what makes the
 * mark-read-on-view effect safe to fire on every thread render.
 *
 * ## The inbox list is patched, never refetched
 *
 * Refreshing the read the way the route announces it (`message.changed`) would refetch
 * every inbox page, every open thread's row, messages, pins — and the list swapped under
 * the user's cursor a few hundred milliseconds after a click.
 *
 * So a read does exactly two things: it patches the cached inbox pages
 * (`patchInboxCacheForRead`, across every page the list has loaded — a read cannot
 * reorder a list sorted by `-lastMessageAt`, so the patch is the whole truth), and it
 * refetches the conversation's OWN queries and the aggregates once. The patch marks the
 * pages fresh, so this tab's own echo of the read leaves them alone too; the list's 15s
 * `staleTime`, its realtime refreshes and `refetchOnWindowFocus` reconcile it with the
 * server on their own schedule.
 *
 * The patch is applied on `onMutate` and undone on error, and it is the same patch for
 * every caller — the row menu's "Mark as read" and a thread marking itself read on view
 * clear a badge and leave an unread-filtered page identically. See `inbox-read-patch.ts`
 * § One rule, however the cursor was advanced.
 */

import { queryKeys, useInvalidateQueries } from "@birdwatch/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { patchInboxCacheForRead } from "../_libs/inbox-read-patch";
import {
  useCreateChannelsMarkUnreadByCid,
  useCreateChannelsReadByCid,
  useGetChannelsUnreadByCid,
} from "./channel-endpoints";
import {
  channelQueryKeys,
  useChannelInvalidate,
} from "./use-channel-invalidate";

/**
 * The aggregates a read or an unread moves: the inbox badge, a home's unread count and
 * the order cards' unread markers. Refetched rather than patched — each is a COUNT the
 * server computes, and off the Chats screen the list cache a patch would count from is
 * not even mounted.
 */
const readAggregateKeys = () => [
  queryKeys.getChannelsInboxCounts(),
  queryKeys.getChannelsInboxPlaces(),
  queryKeys.getOrdersList(),
];

export function useChannelUnread(cid: string, enabled = true) {
  return useGetChannelsUnreadByCid({ cid }).get({ enabled: enabled && !!cid });
}

/** Undoes an optimistic patch; handed from `onMutate` to `onError`. */
type RestoreInboxPages = () => void;

export function useMarkChannelRead(cid: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueries();
  const invalidateChannel = useChannelInvalidate(cid);

  const mutation = useCreateChannelsReadByCid({
    onMutate: async (variables): Promise<RestoreInboxPages> => {
      const inboxKey = queryKeys.getChannelsInbox();
      // An inbox request already in flight would land after the patch and undo it.
      await queryClient.cancelQueries({ queryKey: inboxKey });

      // Pairs of [key, data] — the key is how the patch tells an unread-filtered page
      // from an unfiltered one, which `setQueriesData`'s updater never sees.
      const snapshots = queryClient.getQueriesData<{
        pages: { data?: { cid: string; unreadCount: number }[] }[];
        pageParams: unknown[];
      }>({ queryKey: inboxKey });

      for (const [key, cached] of snapshots) {
        queryClient.setQueryData(
          key,
          patchInboxCacheForRead(cached, key, variables.params.cid),
        );
      }

      return () => {
        for (const [key, cached] of snapshots) {
          queryClient.setQueryData(key, cached);
        }
      };
    },
    onError: (_error, _variables, restore) => {
      (restore as RestoreInboxPages | undefined)?.();
    },
    // The thread's cursor and dots are awaited (returned); the aggregates are not.
    onSuccess: () => {
      void invalidate(...readAggregateKeys());
      return invalidateChannel();
    },
  });

  /**
   * `messageId` names the newest message seen; omit it to mark the whole thread —
   * which is what opening a channel implies.
   */
  const markRead = useCallback(
    (messageId?: string) =>
      mutation.mutateAsync({
        params: { cid },
        body: messageId == null ? {} : { messageId },
      }),
    [mutation, cid],
  );

  return { markRead, isMarkingRead: mutation.isPending };
}

/**
 * "Mark as unread" — undoes the viewer's last read of a conversation
 * (`POST /:cid/mark-unread`): the server restores the unread messages they had before
 * opening it. Takes the `cid` at call time because one list menu acts on many rows.
 *
 * Refetches, rather than patches, the places that show the change — the
 * conversation's own queries (unread cursor, dots), the inbox list, its counts and its
 * homes, and the member home's order cards — because the restored count is the server's
 * to compute. The route announces it only to the caller's own tabs (`message.changed`
 * on their user channel), so this names the keys itself.
 *
 * The caller must not leave that conversation's thread open: an open thread marks itself
 * read on view, which would undo this at once.
 */
export function useMarkChannelUnread() {
  const invalidate = useInvalidateQueries();
  const mutation = useCreateChannelsMarkUnreadByCid({
    // Returned, so `markUnread` resolves once every badge shows the restored count.
    onSuccess: (_data, { params: { cid } }) =>
      invalidate(
        ...channelQueryKeys(cid),
        queryKeys.getChannelsInbox(),
        ...readAggregateKeys(),
      ),
  });

  const markUnread = useCallback(
    (cid: string) => mutation.mutateAsync({ params: { cid } }),
    [mutation],
  );

  return { markUnread, isMarkingUnread: mutation.isPending };
}
